
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import type { Application } from '@/types/database.types';
import { 
  processAdditionalContacts, 
  processPayrollSchedules, 
  ensureStringArray,
  ensureDocumentArray,
  normalizeCorporateStructure,
  processHealthPlans
} from '@/utils/application-data-utils';
import type { Json } from '@/integrations/supabase/types';

export const useApplications = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // State to manage applications for linked users when in broker mode
  const [linkedUserApplications, setLinkedUserApplications] = useState<{
    [userId: string]: Application[]
  }>({});
  
  const fetchApplications = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      let userIdToFetch = user.id;
      
      // If user is a broker, fetch applications for all linked users
      if (userRole === 'broker') {
        const { data: links, error: linksError } = await supabase
          .from('broker_user_links')
          .select('user_id')
          .eq('broker_id', user.id);
          
        if (linksError) throw linksError;
        
        const linkedUsers = links.map(link => link.user_id);
        const linkedAppsMap: { [userId: string]: Application[] } = {};
        
        if (linkedUsers.length > 0) {
          const { data: linkedApps, error: linkedAppsError } = await supabase
            .from('applications')
            .select('*')
            .in('user_id', linkedUsers)
            .order('created_at', { ascending: false });
            
          if (linkedAppsError) throw linkedAppsError;
          
          // Process and group applications by user_id
          linkedApps?.forEach(app => {
            // Process the application data
            const processedApp = {
              ...app,
              additional_contacts: processAdditionalContacts(app.additional_contacts as Json),
              additional_schedules: processPayrollSchedules(app.additional_schedules as Json[]),
              health_plans: processHealthPlans(app.health_plans as Json),
              desired_medical_carriers: ensureStringArray(app.desired_medical_carriers),
              desired_ancillary_carriers: ensureStringArray(app.desired_ancillary_carriers),
              other_documents: ensureDocumentArray(app.other_documents),
              corporate_structure: normalizeCorporateStructure(app.corporate_structure),
              states_with_employees: ensureStringArray(app.states_with_employees),
              top_3_peo_needs: ensureStringArray(app.top_3_peo_needs),
              preferred_peos: ensureStringArray(app.preferred_peos),
              excluded_peos: ensureStringArray(app.excluded_peos),
              states_of_operation: ensureStringArray(app.states_of_operation),
              // Ensure document fields are arrays
              employee_census_file: ensureDocumentArray(app.employee_census_file),
              dependent_census_file: ensureDocumentArray(app.dependent_census_file),
              workers_comp_declaration_file: ensureDocumentArray(app.workers_comp_declaration_file),
              workers_comp_loss_runs_file: ensureDocumentArray(app.workers_comp_loss_runs_file),
              health_insurance_renewal_file: ensureDocumentArray(app.health_insurance_renewal_file),
              sbc_file: ensureDocumentArray(app.sbc_file),
              tax_exempt_status_file: ensureDocumentArray(app.tax_exempt_status_file)
            } as Application;
            
            if (!linkedAppsMap[app.user_id]) {
              linkedAppsMap[app.user_id] = [];
            }
            linkedAppsMap[app.user_id].push(processedApp);
          });
          
          setLinkedUserApplications(linkedAppsMap);
          setApplications(Object.values(linkedAppsMap).flat());
        }
      } else {
        // For non-broker users, fetch their own applications
        const { data, error } = await supabase
          .from('applications')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        // Process the raw data to match the Application type
        const processedData = data?.map(item => ({
          ...item,
          // Convert JSON fields to proper typed arrays
          additional_contacts: processAdditionalContacts(item.additional_contacts as Json),
          additional_schedules: processPayrollSchedules(item.additional_schedules as Json[]),
          // Process health plans properly
          health_plans: processHealthPlans(item.health_plans as Json),
          // Ensure string arrays - convert DB string values to arrays
          desired_medical_carriers: ensureStringArray(item.desired_medical_carriers),
          desired_ancillary_carriers: ensureStringArray(item.desired_ancillary_carriers),
          other_documents: ensureDocumentArray(item.other_documents),
          corporate_structure: normalizeCorporateStructure(item.corporate_structure),
          // Process additional array fields that might be strings in the database
          states_with_employees: ensureStringArray(item.states_with_employees),
          top_3_peo_needs: ensureStringArray(item.top_3_peo_needs),
          preferred_peos: ensureStringArray(item.preferred_peos),
          excluded_peos: ensureStringArray(item.excluded_peos),
          states_of_operation: ensureStringArray(item.states_of_operation),
          // Ensure document fields are arrays
          employee_census_file: ensureDocumentArray(item.employee_census_file),
          dependent_census_file: ensureDocumentArray(item.dependent_census_file),
          workers_comp_declaration_file: ensureDocumentArray(item.workers_comp_declaration_file),
          workers_comp_loss_runs_file: ensureDocumentArray(item.workers_comp_loss_runs_file),
          health_insurance_renewal_file: ensureDocumentArray(item.health_insurance_renewal_file),
          sbc_file: ensureDocumentArray(item.sbc_file),
          tax_exempt_status_file: ensureDocumentArray(item.tax_exempt_status_file)
        })) as Application[];
        
        setApplications(processedData || []);
      }
    } catch (error: any) {
      console.error('Error fetching applications:', error);
      toast({
        title: 'Error fetching applications',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchApplications();
  }, [user, userRole, toast]);
  
  const startNewApplication = async () => {
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please log in to create a new application',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('applications')
        .insert({
          user_id: user.id,
          status: 'draft',
          progress: 0,
          needs_guidance: true
        })
        .select()
        .single();
        
      if (error) throw error;
      
      // Process the new application data
      const processedData = {
        ...data,
        additional_contacts: processAdditionalContacts(data.additional_contacts as Json),
        additional_schedules: processPayrollSchedules(data.additional_schedules as Json[]),
        health_plans: processHealthPlans(data.health_plans as Json),
        desired_medical_carriers: ensureStringArray(data.desired_medical_carriers),
        desired_ancillary_carriers: ensureStringArray(data.desired_ancillary_carriers),
        other_documents: ensureDocumentArray(data.other_documents),
        corporate_structure: normalizeCorporateStructure(data.corporate_structure),
        states_with_employees: ensureStringArray(data.states_with_employees),
        top_3_peo_needs: ensureStringArray(data.top_3_peo_needs),
        preferred_peos: ensureStringArray(data.preferred_peos),
        excluded_peos: ensureStringArray(data.excluded_peos),
        states_of_operation: ensureStringArray(data.states_of_operation),
        // Ensure document fields are arrays
        employee_census_file: ensureDocumentArray(data.employee_census_file),
        dependent_census_file: ensureDocumentArray(data.dependent_census_file),
        workers_comp_declaration_file: ensureDocumentArray(data.workers_comp_declaration_file),
        workers_comp_loss_runs_file: ensureDocumentArray(data.workers_comp_loss_runs_file),
        health_insurance_renewal_file: ensureDocumentArray(data.health_insurance_renewal_file),
        sbc_file: ensureDocumentArray(data.sbc_file),
        tax_exempt_status_file: ensureDocumentArray(data.tax_exempt_status_file)
      } as Application;
      
      // Update local state with the new application
      setApplications(prev => [processedData, ...prev]);
      
      // Navigate to the application page
      navigate(`/application?id=${data.id}`);
    } catch (error: any) {
      console.error('Error creating application:', error);
      toast({
        title: 'Error creating application',
        description: error.message,
        variant: 'destructive'
      });
    }
  };
  
  const getApplicationsForUser = (userId: string): Application[] => {
    return linkedUserApplications[userId] || [];
  };
  
  // Adding the refetch function to refresh applications
  const refetch = async () => {
    return fetchApplications();
  };
  
  return {
    applications,
    isLoading,
    startNewApplication,
    linkedUserApplications,
    getApplicationsForUser,
    refetch // Export the refetch function
  };
};
